/**
 * <park-admin-actions> + <park-admin-action> — a small button cluster attached
 * to one operator row or form (anton/forrest#1248), lifted 1:1 from TWO raw
 * `<button>` clusters in `components/admin/GalleryManager.astro`:
 * `.gm-moves` (`.gm-move--up`/`.gm-move--down`/`.gm-delete`, repeated once per
 * grid row) and `.gm-addform-actions` (`.gm-addform-cancel`/
 * `.gm-addform-confirm`, the add-photo form). Both are the SAME underlying
 * shape — a `role="group"` of small pill/square buttons, one of them styled as
 * the group's primary action and, where a destructive action exists, that one
 * styled dangerous — which is `admin-shell#FR-7`'s "one primary action per
 * group with destructive actions styled as dangerous" verbatim. Keeping them
 * as two private, differently-styled hand-rolled clusters (rather than one
 * `kind=` vocabulary) is exactly the drift FR-7 exists to prevent.
 *
 * `<park-button>` (the pill CTA) does not fit this case: it is sized/spaced
 * for a stand-alone call-to-action, not a compact in-row icon/text cluster —
 * this element is the operator-row-scoped sibling, not a `<park-button>`
 * duplicate.
 *
 * Author syntax (composite, mirrors `<park-admin-list>` + `<park-admin-row>`):
 *   <park-admin-actions aria-label="Siirrä">
 *     <park-admin-action data-action="up" label="↑" title="Ylös" kind="plain"></park-admin-action>
 *     <park-admin-action data-action="down" label="↓" title="Alas" kind="plain"></park-admin-action>
 *     <park-admin-action data-action="delete" label="🗑" title="Poista" kind="destructive"></park-admin-action>
 *   </park-admin-actions>
 *
 * Each rendered `<button>` carries `data-action="<the child's data-action>"`
 * so the page's own delegated click handler can keep dispatching on that
 * value exactly as it did on `.gm-move--up`/`.gm-move--down`/`.gm-delete`
 * class checks; `field-id` (on `<park-admin-action>`) lands on the rendered
 * `<button>`'s `id` when the page needs `getElementById` (the add-photo
 * confirm/cancel pair).
 *
 * Styling lives in wc/css/park-admin-actions.css — no <style> here, no shadow DOM.
 *
 * @dsSpec admin-shell#FR-7 — one primary action per group, destructive styled dangerous.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc, html } from "./base";

const TAG = "park-admin-actions";
const ACTION_TAG = "park-admin-action";

export type ParkAdminActionKind =
	| "plain"
	| "primary"
	| "cancel"
	| "destructive";

export interface ParkAdminActionData {
	action?: string;
	label?: string;
	title?: string;
	ariaLabel?: string;
	kind?: ParkAdminActionKind;
	fieldId?: string;
	disabled?: boolean;
}

/** variant → utility class list, one per `kind` (the FR-7 styling contract). */
function kindClass(kind: string): string {
	switch (kind) {
		case "primary":
			return "pa-action pa-action--primary";
		case "cancel":
			return "pa-action pa-action--cancel";
		case "destructive":
			return "pa-action pa-action--destructive";
		default:
			return "pa-action pa-action--plain";
	}
}

/**
 * Pure render: (ariaLabel, actions) → inner HTML string. No DOM access, so it
 * is safe to call server-side (the .astro wrapper) and client-side (the
 * element parsing its author-syntax children).
 */
export function renderParkAdminActions(
	actions: ParkAdminActionData[],
	groupAriaLabel?: string,
): string {
	const label = groupAriaLabel
		? html` aria-label="${esc(groupAriaLabel)}"`
		: "";
	const buttons = actions
		.map((a) => {
			const id = a.fieldId ? html` id="${esc(a.fieldId)}"` : "";
			const title = a.title ? html` title="${esc(a.title)}"` : "";
			const aria = a.ariaLabel ? html` aria-label="${esc(a.ariaLabel)}"` : "";
			const disabled = a.disabled ? " disabled" : "";
			return html`<button type="button" class="${kindClass(a.kind || "plain")}" data-action="${esc(a.action)}"${id}${title}${aria}${disabled}>${esc(a.label)}</button>`;
		})
		.join("");
	return html`<div class="pa-actions" role="group"${label}>${buttons}</div>`;
}

class ParkAdminActions extends ParkElement {
	static get observedAttributes(): string[] {
		return ["aria-label"];
	}

	protected render(): string {
		const actions: ParkAdminActionData[] = [];
		const tmp = document.createElement("div");
		tmp.innerHTML = this.slotHtml;
		for (const el of tmp.querySelectorAll(ACTION_TAG)) {
			actions.push({
				action: el.getAttribute("data-action") ?? undefined,
				label: el.getAttribute("label") ?? undefined,
				title: el.getAttribute("title") ?? undefined,
				ariaLabel: el.getAttribute("aria-label") ?? undefined,
				kind: (el.getAttribute("kind") as ParkAdminActionKind) || undefined,
				fieldId: el.getAttribute("field-id") ?? undefined,
				disabled: el.hasAttribute("disabled"),
			});
		}
		return renderParkAdminActions(actions, this.attr("aria-label"));
	}
}

// Author-syntax only: parsed by <park-admin-actions>. See park-admin-row.ts /
// park-admin-tile.ts for why this extends ParkElement rather than HTMLElement.
class ParkAdminAction extends ParkElement {
	static get observedAttributes(): string[] {
		return [];
	}
	protected render(): string {
		return this.slotHtml;
	}
}

defineElement(TAG, ParkAdminActions);
defineElement(ACTION_TAG, ParkAdminAction);

export { ParkAdminActions, ParkAdminAction };
